import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import {
    ArrowLeft,
    Save,
    Car,
    Home,
    DollarSign,
    MapPin,
    Tag,
    Image as ImageIcon,
    X,
    AlertCircle,
    Plus,
    ChevronDown,
    Upload,
} from 'lucide-react';
import {
    useCreateDealerListingMutation,
    useGetDealerProfileQuery,
    CreateDealerListingRequest,
} from '../../store/slices/apiSlice';
import { toast } from 'react-hot-toast';
import { Button } from '../../components/ui/Button';

interface LocationSuggestion {
    description: string;
    place_id: string;
    structured_formatting: {
        main_text: string;
        secondary_text: string;
    };
}

const currencySymbols = [
    { symbol: '₦', code: 'NGN', name: 'Nigerian Naira' },
    { symbol: '$', code: 'USD', name: 'US Dollar' },
    { symbol: '€', code: 'EUR', name: 'Euro' },
    { symbol: '£', code: 'GBP', name: 'British Pound' },
    { symbol: '¥', code: 'JPY', name: 'Japanese Yen' },
    { symbol: '₹', code: 'INR', name: 'Indian Rupee' },
    { symbol: '₨', code: 'PKR', name: 'Pakistani Rupee' },
    { symbol: 'R$', code: 'BRL', name: 'Brazilian Real' },
    { symbol: 'C$', code: 'CAD', name: 'Canadian Dollar' },
    { symbol: 'A$', code: 'AUD', name: 'Australian Dollar' },
];

const DealerCreateListing: React.FC = () => {
    const navigate = useNavigate();
    const { user, token } = useSelector((state: RootState) => state.auth);
    const { data: profileData } = useGetDealerProfileQuery(undefined, { skip: !token });
    const profile = profileData?.data;
    
    const [createListing, { isLoading: isSubmitting }] = useCreateDealerListingMutation();

    const dealerType = profile?.dealer_type || 'car';

    const [formData, setFormData] = useState<Omit<CreateDealerListingRequest, 'images'>>({
        listing_type: dealerType,
        title: '',
        description: '',
        price: 0,
        currency_code: profile?.currency_symbol === '$' ? 'USD' : profile?.currency_code || 'NGN',
        currency_symbol: profile?.currency_symbol || '₦',
        location: '',
        tags: [],
        car_details: {},
        house_details: {},
    });

    const [images, setImages] = useState<File[]>([]);
    const [imagePreviews, setImagePreviews] = useState<string[]>([]);
    const [newTag, setNewTag] = useState('');
    const [errors, setErrors] = useState<Record<string, string>>({});
    
    // Location search states
    const [locationSearch, setLocationSearch] = useState('');
    const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);
    const [locationSuggestions, setLocationSuggestions] = useState<LocationSuggestion[]>([]);
    const [isSearchingLocation, setIsSearchingLocation] = useState(false);
    const locationRef = useRef<HTMLDivElement>(null);
    
    // Currency symbol dropdown states
    const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);
    const currencyRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (profile?.currency_symbol) {
            setFormData(prev => ({
                ...prev,
                currency_symbol: profile.currency_symbol || prev.currency_symbol,
                currency_code: profile.currency_code || prev.currency_code,
            }));
        }
    }, [profile]);

    const handleInputChange = (field: keyof typeof formData, value: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        if (errors[field]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    };

    const handleCarDetailChange = (field: string, value: any) => {
        setFormData(prev => ({
            ...prev,
            car_details: {
                ...prev.car_details,
                [field]: field === 'year' || field === 'mileage' ? (value ? Number(value) : undefined) : value
            }
        }));
        if (errors.car_details) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.car_details;
                return newErrors;
            });
        }
    };

    const handleHouseDetailChange = (field: string, value: any) => {
        setFormData(prev => ({
            ...prev,
            house_details: {
                ...prev.house_details,
                [field]: field === 'bedrooms' || field === 'bathrooms' || field === 'area' ? (value ? Number(value) : undefined) : value
            }
        }));
        if (errors.house_details) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.house_details;
                return newErrors;
            });
        }
    };

    const handleAddTag = () => {
        if (newTag.trim() && !formData.tags?.includes(newTag.trim())) {
            handleInputChange('tags', [...(formData.tags || []), newTag.trim()]);
            setNewTag('');
        }
    };

    const handleRemoveTag = (tagToRemove: string) => {
        handleInputChange('tags', formData.tags?.filter(tag => tag !== tagToRemove) || []);
    };

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const newFiles = Array.from(e.target.files);
            const newPreviews = newFiles.map(file => URL.createObjectURL(file));
            setImages(prev => [...prev, ...newFiles]);
            setImagePreviews(prev => [...prev, ...newPreviews]);
        }
    };

    const removeImage = (index: number) => {
        setImages(prev => prev.filter((_, i) => i !== index));
        setImagePreviews(prev => {
            URL.revokeObjectURL(prev[index]);
            return prev.filter((_, i) => i !== index);
        });
    };

    const searchLocation = async (query: string) => {
        if (!query || query.length < 3) {
            setLocationSuggestions([]);
            setShowLocationSuggestions(false);
            return;
        }

        setIsSearchingLocation(true);
        try {
            const response = await fetch(
                `https://maps.googleapis.com/maps/api/place/autocomplete/json?input=${encodeURIComponent(query)}&key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY || ''}`
            );
            const data = await response.json();
            
            if (data.predictions) {
                setLocationSuggestions(data.predictions);
                setShowLocationSuggestions(true);
            } else {
                setLocationSuggestions([]);
                setShowLocationSuggestions(false);
            }
        } catch (error: any) {
            console.error('Location search error:', error);
            setLocationSuggestions([]);
            setShowLocationSuggestions(false);
        } finally {
            setIsSearchingLocation(false);
        }
    };

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (locationSearch) {
                searchLocation(locationSearch);
            } else {
                setLocationSuggestions([]);
                setShowLocationSuggestions(false);
            }
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [locationSearch]);

    const selectLocation = (location: LocationSuggestion) => {
        setLocationSearch(location.description);
        handleInputChange('location', location.description);
        setShowLocationSuggestions(false);
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (locationRef.current && !locationRef.current.contains(event.target as Node)) {
                setShowLocationSuggestions(false);
            }
            if (currencyRef.current && !currencyRef.current.contains(event.target as Node)) {
                setShowCurrencyDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const selectCurrencySymbol = (currency: typeof currencySymbols[0]) => {
        handleInputChange('currency_symbol', currency.symbol);
        handleInputChange('currency_code', currency.code);
        setShowCurrencyDropdown(false);
    };

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!formData.title.trim()) {
            newErrors.title = 'Title is required';
        }

        if (!formData.description.trim()) {
            newErrors.description = 'Description is required';
        }

        if (!formData.price || formData.price <= 0) {
            newErrors.price = 'Price is required and must be greater than 0';
        }

        if (formData.listing_type === 'car') {
            if (!formData.car_details?.vin) {
                newErrors.vin = 'VIN number is required for car listings';
            } else {
                const vinRegex = /^[A-HJ-NPR-Z0-9]{17}$/;
                if (!vinRegex.test(formData.car_details.vin)) {
                    newErrors.vin = 'VIN must be exactly 17 alphanumeric characters (excluding I, O, Q)';
                }
            }
            if (!formData.car_details?.make) {
                newErrors.make = 'Car make is required';
            }
            if (!formData.car_details?.model) {
                newErrors.model = 'Car model is required';
            }
        }

        if (formData.listing_type === 'house') {
            if (!formData.house_details?.bedrooms) {
                newErrors.bedrooms = 'Number of bedrooms is required';
            }
        }

        if (images.length === 0) {
            newErrors.images = 'At least one image is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            toast.error('Please fix the errors in the form');
            return;
        }

        // Check if dealer is approved
        if (profile?.verification_status !== 'approved') {
            toast.error('Your account must be approved before creating listings');
            return;
        }

        try {
            const formDataToSend = new FormData();
            
            formDataToSend.append('listing_type', formData.listing_type);
            formDataToSend.append('title', formData.title);
            formDataToSend.append('description', formData.description);
            formDataToSend.append('price', formData.price.toString());
            if (formData.currency_code) formDataToSend.append('currency_code', formData.currency_code);
            if (formData.currency_symbol) formDataToSend.append('currency_symbol', formData.currency_symbol);
            if (formData.location) formDataToSend.append('location', formData.location);
            
            // Always send tags as an array - backend requires tags to be an array
            // Use tags[] format when tags exist, or send empty array as JSON string
            const tagsToSend = Array.isArray(formData.tags) ? formData.tags.filter(tag => tag && tag.trim()) : [];
            if (tagsToSend.length > 0) {
                // Send as tags[] entries (FormData array format)
                tagsToSend.forEach(tag => {
                    formDataToSend.append('tags[]', tag.trim());
                });
            } else {
                // Send empty array as JSON string to ensure backend receives an array
                formDataToSend.append('tags', JSON.stringify([]));
            }

            // Always send car_details as an object when listing type is 'car'
            // Use bracket notation so backend receives it as an object structure
            if (formData.listing_type === 'car') {
                const carDetails = formData.car_details || {};
                // Always send car_details fields, even if empty, to ensure backend receives an object
                const carFields = ['vin', 'make', 'model', 'year', 'mileage'];
                let hasAnyValue = false;
                carFields.forEach(carKey => {
                    const value = carDetails[carKey];
                    if (value !== undefined && value !== null && value !== '') {
                        formDataToSend.append(`car_details[${carKey}]`, String(value));
                        hasAnyValue = true;
                    }
                });
                // If no values exist, send at least one empty field to establish object structure
                if (!hasAnyValue) {
                    formDataToSend.append('car_details[vin]', '');
                }
            }

            // Always send house_details as an object when listing type is 'house'
            // Use bracket notation so backend receives it as an object structure
            if (formData.listing_type === 'house') {
                const houseDetails = formData.house_details || {};
                // Always send house_details fields, even if empty, to ensure backend receives an object
                const houseFields = ['bedrooms', 'bathrooms', 'area'];
                let hasAnyValue = false;
                houseFields.forEach(houseKey => {
                    const value = houseDetails[houseKey];
                    if (value !== undefined && value !== null && value !== '') {
                        formDataToSend.append(`house_details[${houseKey}]`, String(value));
                        hasAnyValue = true;
                    }
                });
                // If no values exist, send at least one empty field to establish object structure
                if (!hasAnyValue) {
                    formDataToSend.append('house_details[bedrooms]', '');
                }
            }

            images.forEach(image => {
                formDataToSend.append('images', image);
            });

            const response = await createListing(formDataToSend).unwrap();
            
            if (response.success) {
                toast.success('Listing created successfully! Waiting for admin approval.');
                navigate('/dealer/listings');
            }
        } catch (error: any) {
            console.error('Failed to create listing:', error);
            toast.error(error?.data?.message || 'Failed to create listing');
        }
    };

    if (!user || user.role !== 'DEALER') {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-red-600 text-sm">Please log in as a dealer to create listings</div>
            </div>
        );
    }

    if (profile?.verification_status !== 'approved') {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6">
                    <div className="flex items-center gap-3 mb-4">
                        <AlertCircle className="w-8 h-8 text-yellow-600" />
                        <h2 className="text-xl font-semibold text-gray-900">Account Not Approved</h2>
                    </div>
                    <p className="text-gray-600 mb-4">
                        Your dealer account must be approved by admin before you can create listings.
                        {profile?.verification_status === 'pending' && ' Your account is currently under review.'}
                        {profile?.verification_status === 'rejected' && profile?.rejection_reason && (
                            <span className="block mt-2 text-red-600">Reason: {profile.rejection_reason}</span>
                        )}
                    </p>
                    <Button
                        onClick={() => navigate('/dealer/profile')}
                        className="w-full bg-blue-600 hover:bg-blue-700"
                    >
                        Go to Profile
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-white">
            {/* Header */}
            <div className="bg-white border-b border-gray-200">
                <div className="px-6 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={() => navigate('/dealer/listings')}
                                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <ArrowLeft className="w-5 h-5" />
                            </button>
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">Create New Listing</h1>
                                <p className="text-gray-600">Add a new {dealerType} listing</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="px-6 py-6">
                <div className="max-w-4xl mx-auto">
                    <form onSubmit={handleSubmit} className="space-y-8">
                        {/* Basic Information */}
                        <div className="bg-white rounded-lg shadow">
                            <div className="px-6 py-4 border-b border-gray-200">
                                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                                    <Car className="w-5 h-5 mr-2" />
                                    Basic Information
                                </h2>
                            </div>
                            <div className="p-6 space-y-6">
                                {/* Listing Type (Read-only based on dealer type) */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Listing Type *
                                    </label>
                                    <div className="p-4 border-2 border-gray-300 rounded-lg bg-white flex items-center space-x-3">
                                        {dealerType === 'car' ? (
                                            <>
                                                <Car className="w-6 h-6 text-gray-700" />
                                                <span className="font-medium text-gray-700">Car Listing</span>
                                            </>
                                        ) : (
                                            <>
                                                <Home className="w-6 h-6 text-gray-700" />
                                                <span className="font-medium text-gray-700">House Listing</span>
                                            </>
                                        )}
                                    </div>
                                    <p className="mt-1 text-xs text-gray-500">
                                        You can only create {dealerType} listings based on your dealer type.
                                    </p>
                                </div>

                                {/* Title */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Title *
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.title}
                                        onChange={(e) => handleInputChange('title', e.target.value)}
                                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                            errors.title ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                        placeholder={dealerType === 'car' ? "e.g., Toyota Camry 2020" : "e.g., 3 Bedroom House in Lagos"}
                                    />
                                    {errors.title && (
                                        <p className="mt-1 text-sm text-red-600">{errors.title}</p>
                                    )}
                                </div>

                                {/* Description */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Description *
                                    </label>
                                    <textarea
                                        value={formData.description}
                                        onChange={(e) => handleInputChange('description', e.target.value)}
                                        rows={4}
                                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                            errors.description ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                        placeholder="Describe the listing in detail..."
                                    />
                                    {errors.description && (
                                        <p className="mt-1 text-sm text-red-600">{errors.description}</p>
                                    )}
                                </div>

                                {/* Price and Currency */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Price *
                                        </label>
                                        <div className="relative">
                                            <div className="absolute left-3 top-1/2 transform -translate-y-1/2 flex items-center">
                                                <span className="text-gray-700 font-semibold text-lg mr-1">
                                                    {formData.currency_symbol || '₦'}
                                                </span>
                                            </div>
                                            <input
                                                type="number"
                                                value={formData.price}
                                                onChange={(e) => handleInputChange('price', parseFloat(e.target.value) || 0)}
                                                className={`w-full pl-16 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                                    errors.price ? 'border-red-500' : 'border-gray-300'
                                                }`}
                                                placeholder="0.00"
                                                min="0"
                                                step="0.01"
                                            />
                                        </div>
                                        {errors.price && (
                                            <p className="mt-1 text-sm text-red-600">{errors.price}</p>
                                        )}
                                    </div>
                                    <div ref={currencyRef} className="relative">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Currency
                                        </label>
                                        <button
                                            type="button"
                                            onClick={() => setShowCurrencyDropdown(!showCurrencyDropdown)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-left flex items-center justify-between hover:bg-gray-50"
                                        >
                                            <span className="text-gray-900">
                                                {formData.currency_symbol || '₦'}
                                            </span>
                                            <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showCurrencyDropdown ? 'transform rotate-180' : ''}`} />
                                        </button>
                                        
                                        {showCurrencyDropdown && (
                                            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                                {currencySymbols.map((currency, index) => (
                                                    <button
                                                        key={index}
                                                        type="button"
                                                        onClick={() => selectCurrencySymbol(currency)}
                                                        className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0"
                                                    >
                                                        <div className="flex items-center justify-between">
                                                            <div>
                                                                <div className="font-medium text-gray-900">
                                                                    {currency.symbol} - {currency.name}
                                                                </div>
                                                                <div className="text-xs text-gray-500 mt-0.5">
                                                                    {currency.code}
                                                                </div>
                                                            </div>
                                                            {formData.currency_symbol === currency.symbol && (
                                                                <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                                                            )}
                                                        </div>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Location */}
                                <div ref={locationRef}>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Location
                                    </label>
                                    <div className="relative">
                                        <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 z-10" />
                                        <input
                                            type="text"
                                            value={locationSearch}
                                            onChange={(e) => {
                                                setLocationSearch(e.target.value);
                                                handleInputChange('location', e.target.value);
                                            }}
                                            onFocus={() => {
                                                if (locationSuggestions.length > 0) {
                                                    setShowLocationSuggestions(true);
                                                }
                                            }}
                                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="Search for a city (e.g., Lagos, London, New York)"
                                        />
                                        {isSearchingLocation && (
                                            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                            </div>
                                        )}
                                        
                                        {showLocationSuggestions && locationSuggestions.length > 0 && (
                                            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                                {locationSuggestions.map((location, index) => (
                                                    <button
                                                        key={location.place_id || index}
                                                        type="button"
                                                        onClick={() => selectLocation(location)}
                                                        className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0"
                                                    >
                                                        <div className="flex items-start">
                                                            <MapPin className="w-4 h-4 text-gray-400 mt-1 mr-2 flex-shrink-0" />
                                                            <div className="flex-1">
                                                                <div className="font-medium text-gray-900">
                                                                    {location.structured_formatting?.main_text || location.description}
                                                                </div>
                                                                {location.structured_formatting?.secondary_text && (
                                                                    <div className="text-sm text-gray-500 mt-0.5">
                                                                        {location.structured_formatting.secondary_text}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Tags */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Tags
                                    </label>
                                    <div className="flex flex-wrap gap-2 mb-2">
                                        {formData.tags?.map((tag, index) => (
                                            <span
                                                key={index}
                                                className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                                            >
                                                {tag}
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveTag(tag)}
                                                    className="ml-2 text-blue-600 hover:text-blue-800"
                                                >
                                                    <X className="w-3 h-3" />
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            value={newTag}
                                            onChange={(e) => setNewTag(e.target.value)}
                                            onKeyPress={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    handleAddTag();
                                                }
                                            }}
                                            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="Add a tag and press Enter"
                                        />
                                        <button
                                            type="button"
                                            onClick={handleAddTag}
                                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                        >
                                            <Plus className="w-5 h-5" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Car Details */}
                        {formData.listing_type === 'car' && (
                            <div className="bg-white rounded-lg shadow">
                                <div className="px-6 py-4 border-b border-gray-200">
                                    <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                                        <Car className="w-5 h-5 mr-2" />
                                        Car Details
                                    </h2>
                                </div>
                                <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            VIN Number *
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.car_details?.vin || ''}
                                            onChange={(e) => handleCarDetailChange('vin', e.target.value.toUpperCase())}
                                            maxLength={17}
                                            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                                errors.vin ? 'border-red-500' : 'border-gray-300'
                                            }`}
                                            placeholder="17-character VIN"
                                        />
                                        {errors.vin && (
                                            <p className="mt-1 text-sm text-red-600">{errors.vin}</p>
                                        )}
                                        <p className="mt-1 text-xs text-gray-500">
                                            VIN must be exactly 17 characters (excluding I, O, Q)
                                        </p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Make *</label>
                                        <input
                                            type="text"
                                            value={formData.car_details?.make || ''}
                                            onChange={(e) => handleCarDetailChange('make', e.target.value)}
                                            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                                errors.make ? 'border-red-500' : 'border-gray-300'
                                            }`}
                                            placeholder="e.g., Toyota"
                                        />
                                        {errors.make && (
                                            <p className="mt-1 text-sm text-red-600">{errors.make}</p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Model *</label>
                                        <input
                                            type="text"
                                            value={formData.car_details?.model || ''}
                                            onChange={(e) => handleCarDetailChange('model', e.target.value)}
                                            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                                errors.model ? 'border-red-500' : 'border-gray-300'
                                            }`}
                                            placeholder="e.g., Camry"
                                        />
                                        {errors.model && (
                                            <p className="mt-1 text-sm text-red-600">{errors.model}</p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Year</label>
                                        <input
                                            type="number"
                                            value={formData.car_details?.year || ''}
                                            onChange={(e) => handleCarDetailChange('year', e.target.value)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="e.g., 2020"
                                            min="1900"
                                            max={new Date().getFullYear() + 1}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Mileage</label>
                                        <input
                                            type="number"
                                            value={formData.car_details?.mileage || ''}
                                            onChange={(e) => handleCarDetailChange('mileage', e.target.value)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="e.g., 50000"
                                            min="0"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* House Details */}
                        {formData.listing_type === 'house' && (
                            <div className="bg-white rounded-lg shadow">
                                <div className="px-6 py-4 border-b border-gray-200">
                                    <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                                        <Home className="w-5 h-5 mr-2" />
                                        House Details
                                    </h2>
                                </div>
                                <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Bedrooms *
                                        </label>
                                        <input
                                            type="number"
                                            value={formData.house_details?.bedrooms || ''}
                                            onChange={(e) => handleHouseDetailChange('bedrooms', e.target.value)}
                                            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                                errors.bedrooms ? 'border-red-500' : 'border-gray-300'
                                            }`}
                                            placeholder="e.g., 3"
                                            min="1"
                                        />
                                        {errors.bedrooms && (
                                            <p className="mt-1 text-sm text-red-600">{errors.bedrooms}</p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Bathrooms
                                        </label>
                                        <input
                                            type="number"
                                            value={formData.house_details?.bathrooms || ''}
                                            onChange={(e) => handleHouseDetailChange('bathrooms', e.target.value)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="e.g., 2"
                                            min="1"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Area (sq ft)
                                        </label>
                                        <input
                                            type="number"
                                            value={formData.house_details?.area || ''}
                                            onChange={(e) => handleHouseDetailChange('area', e.target.value)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="e.g., 2000"
                                            min="0"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Images */}
                        <div className="bg-white rounded-lg shadow">
                            <div className="px-6 py-4 border-b border-gray-200">
                                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                                    <ImageIcon className="w-5 h-5 mr-2" />
                                    Images *
                                </h2>
                            </div>
                            <div className="p-6">
                                {errors.images && (
                                    <p className="mb-4 text-sm text-red-600">{errors.images}</p>
                                )}
                                
                                {imagePreviews.length > 0 && (
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                                        {imagePreviews.map((preview, index) => (
                                            <div key={index} className="relative group">
                                                <img
                                                    src={preview}
                                                    alt={`Preview ${index + 1}`}
                                                    className="w-full h-32 object-cover rounded-lg border border-gray-300"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => removeImage(index)}
                                                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                                                >
                                                    <X className="w-4 h-4" />
                                                </button>
                                                {index === 0 && (
                                                    <span className="absolute bottom-2 left-2 bg-blue-500 text-white text-xs px-2 py-1 rounded">
                                                        Primary
                                                    </span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 transition-colors bg-gray-50">
                                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                        <Upload className="w-8 h-8 text-gray-400 mb-2" />
                                        <p className="text-sm text-gray-600">
                                            <span className="font-semibold">Click to upload</span> or drag and drop
                                        </p>
                                        <p className="text-xs text-gray-500 mt-1">
                                            PNG, JPG, GIF up to 10MB
                                        </p>
                                    </div>
                                    <input
                                        type="file"
                                        className="hidden"
                                        accept="image/*"
                                        multiple
                                        onChange={handleImageChange}
                                    />
                                </label>
                            </div>
                        </div>

                        {/* Submit Button */}
                        <div className="flex justify-end gap-4">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => navigate('/dealer/listings')}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={isSubmitting}
                                className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
                            >
                                {isSubmitting ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                        <span>Creating...</span>
                                    </>
                                ) : (
                                    <>
                                        <Save className="w-4 h-4" />
                                        <span>Create Listing</span>
                                    </>
                                )}
                            </Button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default DealerCreateListing;
